// Registers the service worker (offline app shell + push). Opening the app (or bringing it to the
// front) checks for a new version. If it's ready before the first tap, the app reloads into it
// at once; once someone is using the app, a toast offers Update instead, so an update never
// interrupts someone mid-edit. Going to the background also applies a waiting update.

import { getStore } from '../data/store.ts';
import { navigate } from './router.tsx';

/** A new version that's ready within this long of opening, before any tap, is applied at once. */
const FRESH_MS = 15_000;
/** Chromium can defer a skipWaiting that lands while the old worker is handling the page's opening
 *  requests, then still not activate the new one minutes later (sending it again does nothing).
 *  So a fresh open lets those requests through first. */
const SETTLE_MS = 1_000;

let registration: ServiceWorkerRegistration | undefined;
let openedAt = Date.now();
let touched = false;
let offered = false;

function activateWaiting(): void {
  registration?.waiting?.postMessage({ type: 'SKIP_WAITING' });
}

/** A new worker finished installing and waits: apply it now, or offer it. */
function ready(settled = false): void {
  if (!registration?.waiting || !navigator.serviceWorker.controller) return;
  if (!touched && Date.now() - openedAt < FRESH_MS) {
    if (settled) return activateWaiting();
    setTimeout(() => ready(true), SETTLE_MS);
    return;
  }
  if (offered) return;
  offered = true;
  getStore().toast('A new version of the app is ready', 'Update', activateWaiting);
}

function watch(reg: ServiceWorkerRegistration): void {
  const follow = (next: ServiceWorker | null) =>
    next?.addEventListener('statechange', () => {
      if (next.state === 'installed') ready();
    });
  reg.addEventListener('updatefound', () => follow(reg.installing));
  // Opening the app makes the browser check too, so a new version may already be installing.
  follow(reg.installing);
  ready();
}

/** Settings → Check for updates: 'updating' when a new version was found (the app reloads into
 *  it), 'current' when this is the newest. */
export async function checkForUpdate(): Promise<'updating' | 'current' | 'unavailable'> {
  const reg = registration;
  if (!reg) return 'unavailable';
  await reg.update().catch(() => undefined);
  const next = reg.installing ?? reg.waiting;
  if (!next) return 'current';
  if (next.state !== 'installed') await new Promise<void>((r) => next.addEventListener('statechange', () => next.state !== 'installing' && r()));
  reg.waiting?.postMessage({ type: 'SKIP_WAITING' });
  return 'updating';
}

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator) || import.meta.env.DEV) return;
  // A tapped notification while the app is open: go to what it's about.
  navigator.serviceWorker.addEventListener('message', (event) => {
    const data = event.data as { type?: string; url?: string } | undefined;
    if (data?.type === 'OPEN' && data.url?.startsWith('/')) navigate(data.url);
  });
  for (const g of ['pointerdown', 'keydown'] as const) window.addEventListener(g, () => (touched = true), { capture: true, passive: true });
  window.addEventListener('load', async () => {
    try {
      const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      registration = reg;
      watch(reg);
      void reg.update().catch(() => undefined); // offline: try again next time
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') activateWaiting();
        else {
          openedAt = Date.now();
          touched = false;
          void reg.update().catch(() => undefined); // offline: try again next time
        }
      });
      let reloaded = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (reloaded) return;
        reloaded = true;
        window.location.reload();
      });
    } catch (e) {
      console.warn('Service worker registration failed', e);
    }
  });
}
